import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { TripItem, TripWithItems } from '@shared/types';
import { ITEM_TYPES, ITEM_TYPE_LABELS } from '@shared/types';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDateRange } from '../lib/format';
import { ItemCard } from '../components/ItemCard';
import { ImportPanel } from '../components/ImportPanel';

/**
 * Creating a trip is a conversation.
 *
 * Three quick taps establish where/when/what-it's-called, then the traveler
 * just talks: Claude asks what a curious friend would ask, finds each named
 * place's own website while they type, and files cards into the page — which
 * assembles live below the chat, using the same components the share page
 * renders. Notes stay in the traveler's own words; Claude organizes, it never
 * writes.
 *
 * The paste door remains the shortcut for people who already typed it all out
 * once for a friend.
 */

type Phase = 'where' | 'when' | 'title' | 'chat';

interface ChatMessage {
  role: 'user' | 'assistant';
  text: string;
}

/** "Crested Butte, CO" reads better as "Crested Butte" inside a sentence. */
const placePart = (destination: string): string =>
  destination.split(',')[0]?.trim() || destination.trim();

function suggestTitle(
  destination: string,
  startDate: string | null,
  endDate: string | null,
  monthOnly: boolean,
): string {
  const place = withArticle(placePart(destination));
  if (!place) return 'My trip';

  if (startDate && endDate) {
    const days =
      Math.round(
        (Date.parse(`${endDate}T12:00:00Z`) - Date.parse(`${startDate}T12:00:00Z`)) / 86_400_000,
      ) + 1;
    const span = spanPhrase(days);
    if (span) return `How to spend ${span} in ${place}`;
  }
  if (monthOnly && startDate) {
    const month = new Intl.DateTimeFormat('en-US', { month: 'long', timeZone: 'UTC' }).format(
      new Date(`${startDate}T12:00:00Z`),
    );
    return `How to spend ${month} in ${place}`;
  }
  return `A trip to ${place}`;
}

/** "two weeks", "five days", "a weekend" — the guide-headline duration. */
function spanPhrase(days: number): string | null {
  if (days < 1) return null;
  if (days <= 3) return 'a weekend';
  if (days <= 6) {
    const words = ['four', 'five', 'six'];
    return `${words[days - 4]} days`;
  }
  if (days <= 9) return 'a week';
  if (days <= 11) return 'ten days';
  if (days <= 17) return 'two weeks';
  if (days <= 24) return 'three weeks';
  if (days <= 36) return 'a month';
  return `${Math.round(days / 7)} weeks`;
}

/** "the British Virgin Islands", but "Paris" stays bare. Suggestion only —
 *  the traveler edits the title before it sticks. */
function withArticle(place: string): string {
  if (!place) return place;
  if (/^the\s/i.test(place)) return place;
  const needsThe =
    /(islands?|keys|alps|rockies|hamptons|banks|netherlands|philippines|maldives|bahamas|azores|catskills|poconos|berkshires|everglades|hill country|dominican republic|united states|usa|uk)$/i;
  return needsThe.test(place) ? `the ${place}` : place;
}

function recentMonths(count: number): Array<{ ym: string; label: string }> {
  const now = new Date();
  return Array.from({ length: count }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label =
      i === 0
        ? 'This month'
        : d.toLocaleDateString('en-US', {
            month: 'long',
            ...(d.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}),
          });
    return { ym, label };
  });
}

export function NewTripPage() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  const [phase, setPhase] = useState<Phase>('where');

  // Trip facts gathered by the quick taps.
  const [destination, setDestination] = useState('');
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [monthOnly, setMonthOnly] = useState(false);
  const [exactMode, setExactMode] = useState(false);
  const [title, setTitle] = useState('');

  // The page being built.
  const [trip, setTrip] = useState<TripWithItems | null>(null);
  const [items, setItems] = useState<TripItem[]>([]);

  // The conversation.
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [importing, setImporting] = useState(false);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const focusRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!loading && !user) navigate('/login');
  }, [loading, user, navigate]);

  useEffect(() => {
    focusRef.current?.focus();
  }, [phase]);

  // Keep the latest exchange in view without yanking the whole page.
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ block: 'nearest' });
  }, [chatMessages, sending]);

  // ---------------------------------------------------------------------------
  // Quick-tap phases
  // ---------------------------------------------------------------------------

  function submitWhere(e: React.FormEvent) {
    e.preventDefault();
    if (!destination.trim()) return;
    setPhase('when');
  }

  function chooseMonth(ym: string) {
    setStartDate(`${ym}-01`);
    setEndDate(null);
    setMonthOnly(true);
    enterTitle(`${ym}-01`, null, true);
  }

  function submitExactDates(e: React.FormEvent) {
    e.preventDefault();
    if (!startDate) return;
    setMonthOnly(false);
    enterTitle(startDate, endDate, false);
  }

  function skipWhen() {
    setStartDate(null);
    setEndDate(null);
    setMonthOnly(false);
    enterTitle(null, null, false);
  }

  function enterTitle(start: string | null, end: string | null, month: boolean) {
    setTitle(suggestTitle(destination, start, end, month));
    setPhase('title');
  }

  async function submitTitle(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const created = await api.createTrip({
        title: title.trim(),
        destination: destination.trim() || null,
        startDate,
        endDate,
      });
      setTrip(created);
      // The opener is canned client-side: instant, free, and it sets the tone.
      setChatMessages([
        {
          role: 'assistant',
          text: `Where did you stay in ${placePart(destination) || 'town'}? Just name the place — or paste a link if you have one handy.`,
        },
      ]);
      setPhase('chat');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not start the page.');
    } finally {
      setBusy(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Chat
  // ---------------------------------------------------------------------------

  async function refreshWhilePending(tripId: string) {
    await api.enrichTrip(tripId).catch(() => {});
    for (let i = 0; i < 4; i++) {
      await new Promise((r) => setTimeout(r, 3000));
      const fresh = await api.getTrip(tripId).catch(() => null);
      if (!fresh) return;
      setItems(fresh.items);
      if (!fresh.items.some((it) => it.unfurlStatus === 'pending')) return;
    }
  }

  async function sendChat(e: React.FormEvent) {
    e.preventDefault();
    const text = draft.trim();
    if (!trip || !text || sending) return;

    const nextHistory: ChatMessage[] = [...chatMessages, { role: 'user', text }];
    setChatMessages(nextHistory);
    setDraft('');
    setSending(true);
    setError(null);

    try {
      const result = await api.chatTrip(
        trip.id,
        nextHistory.slice(-30), // plenty of context, bounded cost
      );
      setChatMessages((prev) => [...prev, { role: 'assistant', text: result.reply }]);
      setItems(result.items);
      if (result.items.some((it) => it.unfurlStatus === 'pending')) {
        void refreshWhilePending(trip.id);
      }
    } catch (err) {
      // Put their words back in the box rather than losing them.
      setChatMessages((prev) => prev.slice(0, -1));
      setDraft(text);
      setError(err instanceof ApiError ? err.message : 'That didn’t go through. Try again.');
    } finally {
      setSending(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (loading || !user) return <main className="mx-auto max-w-2xl px-5 py-16" />;

  return (
    <main className="mx-auto max-w-2xl px-5 py-12 pb-24">
      {phase === 'where' && (
        <form onSubmit={submitWhere}>
          <h1 className="font-display text-title text-ink">Where did you go?</h1>
          <input
            ref={focusRef as React.RefObject<HTMLInputElement>}
            className="field mt-5 !text-[1.125rem] !py-3"
            placeholder="Crested Butte, Colorado"
            value={destination}
            onChange={(e) => setDestination(e.target.value)}
            autoComplete="off"
          />
          <button
            type="submit"
            disabled={!destination.trim()}
            className="btn-primary mt-4 disabled:opacity-50"
          >
            Next
          </button>
        </form>
      )}

      {phase === 'when' && (
        <div>
          <h1 className="font-display text-title text-ink">When was that?</h1>
          <p className="mt-2 font-body text-muted">Roughly is fine.</p>

          <div className="mt-5 flex flex-wrap gap-2">
            {recentMonths(4).map((m) => (
              <button
                key={m.ym}
                onClick={() => chooseMonth(m.ym)}
                className="rounded-btn bg-raised px-4 py-2 font-util text-[0.9375rem] font-medium
                           text-ink hover:bg-accent hover:text-surface transition-colors"
              >
                {m.label}
              </button>
            ))}
            <input
              type="month"
              aria-label="Pick a month"
              className="field w-auto !py-2"
              onChange={(e) => e.target.value && chooseMonth(e.target.value)}
            />
          </div>

          {exactMode ? (
            <form onSubmit={submitExactDates} className="mt-4 flex flex-wrap items-end gap-3">
              <input
                type="date"
                aria-label="First day"
                className="field w-auto"
                onChange={(e) => setStartDate(e.target.value || null)}
              />
              <input
                type="date"
                aria-label="Last day"
                className="field w-auto"
                min={startDate ?? undefined}
                onChange={(e) => setEndDate(e.target.value || null)}
              />
              <button type="submit" disabled={!startDate} className="btn-primary disabled:opacity-50">
                Next
              </button>
            </form>
          ) : (
            <button onClick={() => setExactMode(true)} className="btn-quiet mt-4 !text-meta">
              I know the exact dates
            </button>
          )}

          <div className="mt-8">
            <button onClick={skipWhen} className="btn-quiet !text-meta text-muted">
              Skip this
            </button>
          </div>
        </div>
      )}

      {phase === 'title' && (
        <form onSubmit={submitTitle}>
          <h1 className="font-display text-title text-ink">Name the page</h1>
          <p className="mt-2 font-body text-muted">We took a guess. Change it or keep it.</p>
          <input
            ref={focusRef as React.RefObject<HTMLInputElement>}
            className="field mt-5 !font-display !text-[1.375rem] !py-3"
            value={title}
            maxLength={140}
            onChange={(e) => setTitle(e.target.value)}
            onFocus={(e) => e.target.select()}
          />
          <button
            type="submit"
            disabled={!title.trim() || busy}
            className="btn-primary mt-4 disabled:opacity-50"
          >
            {busy ? 'Starting the page…' : 'Start the page'}
          </button>
          {error && <p className="mt-3 font-util text-meta text-[#B4462F]">{error}</p>}
        </form>
      )}

      {phase === 'chat' && trip && (
        <div>
          {/* The conversation. Claude's turns read as prose; yours sit on the
              raised panel — the same distinction the trip page draws between
              machine-known and human-written. */}
          <div className="space-y-4">
            {chatMessages.map((m, idx) =>
              m.role === 'assistant' ? (
                <p key={idx} className="font-body text-[1.0625rem] leading-relaxed text-ink">
                  {m.text}
                </p>
              ) : (
                <p
                  key={idx}
                  className="ml-8 rounded-note bg-raised px-4 py-2.5 font-util text-[0.9375rem] text-ink"
                >
                  {m.text}
                </p>
              ),
            )}
            {sending && (
              <p className="font-util text-meta text-muted" role="status">
                Finding that…
              </p>
            )}
            <div ref={chatEndRef} />
          </div>

          <form onSubmit={sendChat} className="mt-5">
            <textarea
              ref={focusRef as React.RefObject<HTMLTextAreaElement>}
              className="field min-h-[4rem] resize-y font-body"
              placeholder="Type it like you'd text a friend"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void sendChat(e);
                }
              }}
            />
            <div className="mt-2 flex items-center gap-2">
              <button
                type="submit"
                disabled={sending || !draft.trim()}
                className="btn-primary disabled:opacity-50"
              >
                Send
              </button>
              <button
                type="button"
                onClick={() => navigate(`/t/${trip.slug}?new=1`)}
                className="btn-quiet ml-auto"
              >
                Finish the page
              </button>
            </div>
            {error && <p className="mt-2 font-util text-meta text-[#B4462F]">{error}</p>}
          </form>

          {importing ? (
            <div className="mt-5">
              <ImportPanel
                tripId={trip.id}
                onImported={(imported) => {
                  setImporting(false);
                  setItems((prev) => [...prev, ...imported]);
                  setChatMessages((prev) => [
                    ...prev,
                    {
                      role: 'assistant',
                      text: `Sorted — ${imported.length} card${imported.length === 1 ? '' : 's'} added below. Anything they miss?`,
                    },
                  ]);
                  void refreshWhilePending(trip.id);
                }}
                onCancel={() => setImporting(false)}
              />
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setImporting(true)}
              className="btn-quiet mt-4 !text-meta text-muted"
            >
              Already typed this out for someone? Paste it all at once
            </button>
          )}

          {/* The page so far — the real thing, growing as they talk. */}
          <div className="mt-12 border-t border-ink/10 pt-8">
            <h2 className="font-display text-[1.5rem] leading-tight text-ink">{trip.title}</h2>
            <p className="mt-1 font-util text-meta text-muted">
              {[trip.destination, formatDateRange(trip.startDate, trip.endDate)]
                .filter(Boolean)
                .join(' · ')}
            </p>

            {items.length === 0 ? (
              <p className="mt-6 font-body text-muted">
                Everything you mention lands here, ready to send.
              </p>
            ) : (
              ITEM_TYPES.map((type) => {
                const group = items.filter((i) => i.type === type);
                if (!group.length) return null;
                return (
                  <section key={type} className="mt-8">
                    <h3 className="font-util text-section font-semibold text-ink/70">
                      {ITEM_TYPE_LABELS[type].plural}
                    </h3>
                    <div className="mt-1 divide-y divide-ink/8">
                      {group.map((item) => (
                        <div key={item.id} className="rise-in">
                          <ItemCard item={item} />
                        </div>
                      ))}
                    </div>
                  </section>
                );
              })
            )}
          </div>
        </div>
      )}
    </main>
  );
}
